'use client';

import { useRef, useState, type ReactNode } from 'react';
import { ImageIcon, Pencil, Upload, ImagePlus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover';
import { AssetPickerDialog } from '@/tenants/_shared/components/AssetPickerDialog';
import { useAssetUpload } from '@/tenants/_shared/hooks/use-asset-upload';
import { cn } from '@/lib/utils';

interface Props {
  src?: string;
  alt?: string;
  className?: string;
  imgClassName?: string;
  isAutoAspect?: boolean;
  assetType?: 'image' | 'video';
  onChange: (next: { src: string; alt: string }) => void;
  onRemove?: () => void;
  emptyLabel?: string;
  children?: ReactNode;
}

export function EditableMediaCell({
  src,
  alt = '',
  className,
  imgClassName,
  isAutoAspect = false,
  assetType = 'image',
  onChange,
  onRemove,
  emptyLabel = 'Click to add image',
}: Props) {
  const { upload, isUploading } = useAssetUpload();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasSrc = Boolean(src);

  const handleFile = async (file: File) => {
    const result = await upload({
      file,
      assetType,
      ownerType: 'tenant',
      uploadedByType: 'dashboard',
      tags: [`page-block-gallery`],
    });
    onChange({ src: result.asset.cdnUrl, alt });
    setPopoverOpen(false);
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={hasSrc ? 'Change image' : emptyLabel}
            className={cn(
              'group relative w-full overflow-hidden text-left',
              !hasSrc &&
                'flex flex-col items-center justify-center gap-2 border-2 border-dashed border-muted-foreground/30 bg-muted/40 text-muted-foreground hover:border-primary hover:text-primary',
              className,
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {hasSrc ? (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={src}
                  alt={alt}
                  className={cn(
                    isAutoAspect ? 'h-auto w-full' : 'absolute inset-0 h-full w-full object-cover',
                    imgClassName,
                  )}
                />
                <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                  <span className="inline-flex items-center gap-1 rounded-md bg-background px-2 py-1 text-xs font-medium text-foreground shadow-sm">
                    <Pencil className="h-3 w-3" />
                    Change
                  </span>
                </span>
              </>
            ) : (
              <>
                <ImageIcon className="h-8 w-8 opacity-50" />
                <span className="text-xs">{emptyLabel}</span>
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="center" className="w-56 p-2" onClick={(e) => e.stopPropagation()}>
          <div className="space-y-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-xs"
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-3.5 w-3.5" />
              {isUploading ? 'Uploading…' : 'Upload from device'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-xs"
              onClick={() => {
                setPopoverOpen(false);
                setPickerOpen(true);
              }}
            >
              <ImagePlus className="mr-2 h-3.5 w-3.5" />
              Browse gallery
            </Button>
            {hasSrc && onRemove ? (
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start text-xs text-destructive hover:text-destructive"
                onClick={() => {
                  onRemove();
                  setPopoverOpen(false);
                }}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Remove
              </Button>
            ) : null}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={assetType === 'image' ? 'image/*' : 'video/*'}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
              e.target.value = '';
            }}
          />
        </PopoverContent>
      </Popover>
      <AssetPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onAssetSelected={(asset) => {
          onChange({ src: asset.cdnUrl, alt });
        }}
        assetType={assetType}
        ownerType="tenant"
        tags={[`page-block-${assetType}`]}
        uploadedByType="dashboard"
        title="Select image"
      />
    </>
  );
}
